/*
  Breed hub: client-side search, size filter and rank ordering over the server-rendered cards.

  The grid section renders the first 250 published breeds (Liquid paginates
  metaobject_definition.values at 250 a page). If there are more pages, they are fetched with the
  Section Rendering API and their cards appended, so search covers every breed on one page and the
  server pagination links stay as the no-JS fallback. Cards carry data-name / data-group /
  data-size / data-rank (see snippets/c-breed-card.liquid).
*/
(function () {
  'use strict';

  // A card's identity is its breed page URL (c-breed-card.liquid links breed.system.url).
  const breedKey = (card) => {
    const link = card.querySelector('.breed-card__link');
    return (link && link.getAttribute('href')) || card.dataset.name;
  };

  class BreedHub extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-breed-hub-list]');
      this.count = this.querySelector('[data-breed-hub-count]');
      this.empty = this.querySelector('[data-breed-hub-empty]');
      this.pagesNav = this.querySelector('[data-breed-hub-pages]');
      if (!this.list) return;

      this.size = 'All';
      this.controller = new AbortController();
      const signal = this.controller.signal;
      // The controls only make sense over the COMPLETE catalog: they stay inert until every
      // pagination page has loaded on page 1. On a deeper server page, or after a failed page
      // fetch, the grid stays as the server rendered it - its count and its pagination links -
      // rather than presenting one page as the whole catalog.
      this.ready = false;

      // The search band is a SIBLING section, so its controls are outside this
      // element's lifecycle: re-rendering the search section in the Theme Editor
      // replaces those nodes without ever disconnecting the grid. Binding
      // directly to them would leave this element holding listeners on detached
      // nodes while the live controls have none. Delegating from `document`
      // instead means the current controls always work, whichever section was
      // last re-rendered, and the AbortController still removes everything when
      // the grid itself goes away.
      document.addEventListener('submit', (e) => {
        if (this.ready && e.target instanceof Element && e.target.closest('[data-breed-hub-form]')) {
          e.preventDefault();
        }
      }, { signal });

      document.addEventListener('input', (e) => {
        if (this.ready && e.target instanceof Element && e.target.closest('[data-breed-hub-query]')) {
          this.apply();
        }
      }, { signal });

      // The search section can be re-rendered on its own in the Theme Editor, which replaces the
      // input without reconnecting this element - so the count-aware placeholder set at connect
      // time would be lost. Re-apply it whenever any section reloads.
      document.addEventListener('shopify:section:load', () => {
        if (this.ready) this.setPlaceholder();
      }, { signal });

      document.addEventListener('click', (e) => {
        if (!this.ready || !(e.target instanceof Element)) return;
        const chip = e.target.closest('[data-breed-hub-size]');
        if (!chip) return;
        this.size = chip.dataset.breedHubSize || 'All';
        this.sizeChips().forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
        this.apply();
      }, { signal });

      this.loadRemainingPages().then(
        () => {
          this.ready = true;
          this.sortByRank();
          this.setPlaceholder();
          this.apply();
        },
        () => {}
      );
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
    }

    cards() {
      return Array.from(this.list.querySelectorAll('[data-breed-card]'));
    }

    // Resolved on demand, never cached: the search section can be re-rendered at
    // any time, which replaces these nodes.
    queryInput() {
      return document.querySelector('[data-breed-hub-query]');
    }

    sizeChips() {
      return Array.from(document.querySelectorAll('[data-breed-hub-size]'));
    }

    async loadRemainingPages() {
      const pages = Number(this.dataset.pages || 1);
      const current = Number(this.dataset.page || 1);
      const sectionId = this.dataset.sectionId;
      if (pages <= 1) return;
      // A deeper server page is a partial view by definition; rejecting keeps the controls inert.
      if (current !== 1 || !sectionId) throw new Error('breed-hub: not on the first page');
      // Collect every page first and append once. Appending per page meant a failure on page 3
      // left pages 1-2 in the grid with the controls inert and the pagination links pointing at
      // a list that no longer matched the server's page 1.
      const fetched = document.createDocumentFragment();
      // Skip breeds already in the grid. A second run in one page life, or a page 1 restored from
      // cache beside a fresh page 2 after a breed published, would otherwise show a breed twice.
      const seen = new Set(this.cards().map(breedKey));
      for (let page = 2; page <= pages; page++) {
        const url = new URL(window.location.href);
        // Metaobject lists paginate on a keyed param (page_<id>), not `page`. Setting `page` made
        // every fetch return page 1 again, so the first 250 breeds were appended twice.
        url.searchParams.set(this.dataset.pageParam || 'page', String(page));
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url.toString(), { signal: this.controller.signal });
        if (!response.ok) throw new Error('breed-hub: page ' + page + ' failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        doc.querySelectorAll('[data-breed-card]').forEach((card) => {
          const key = breedKey(card);
          if (seen.has(key)) return;
          seen.add(key);
          fetched.appendChild(card);
        });
      }
      this.list.appendChild(fetched);
      if (this.pagesNav) {
        // Hand focus off before hiding. A keyboard user can reach a pagination link while these
        // fetches are still in flight; hiding its ancestor underneath them drops focus to the
        // document and loses their place silently.
        if (this.pagesNav.contains(document.activeElement)) {
          const query = this.queryInput();
          if (query) query.focus();
          // Only hide once focus has actually LANDED somewhere. The search section is optional -
          // a merchant can remove it, and the input can be hidden or disabled - so "we called
          // focus()" is not the same as "focus moved". If it did not, the redundant nav stays
          // visible, which costs a row of links and keeps the user where they were.
          if (!this.pagesNav.contains(document.activeElement)) this.pagesNav.hidden = true;
        } else {
          this.pagesNav.hidden = true;
        }
      }
    }

    sortByRank() {
      const cards = this.cards();
      cards.sort((a, b) => {
        const diff = Number(a.dataset.rank) - Number(b.dataset.rank);
        return diff || (a.dataset.name || '').localeCompare(b.dataset.name || '');
      });
      cards.forEach((card) => this.list.appendChild(card));
    }

    setPlaceholder() {
      const query = this.queryInput();
      if (!query) return;
      const pattern = query.dataset.placeholderPattern;
      if (!pattern || pattern.indexOf('[count]') === -1) return;
      const count = this.cards().length;
      // "Search 1 breeds" is wrong every time the hub holds exactly one published breed, which is
      // the normal state during a staged rollout. Only the SHIPPED phrase is singularised: the
      // setting takes arbitrary merchant copy, and a generic "strip the trailing s" rule turns
      // "[count] species" into "1 specie" while silently missing "[count] dog breeds". Anything
      // the merchant rewrites is left exactly as typed.
      const text = count === 1 ? pattern.replace('[count] breeds', '[count] breed') : pattern;
      query.placeholder = text.replace('[count]', String(count));
    }

    apply() {
      const query = this.queryInput();
      const q = ((query && query.value) || '').trim().toLowerCase();
      let shown = 0;
      this.cards().forEach((card) => {
        const sizeOk = this.size === 'All' || card.dataset.size === this.size;
        const name = (card.dataset.name || '').toLowerCase();
        const group = (card.dataset.group || '').toLowerCase();
        const queryOk = !q || name.indexOf(q) !== -1 || group.indexOf(q) !== -1;
        const ok = sizeOk && queryOk;
        card.hidden = !ok;
        if (ok) shown++;
      });
      if (this.count) this.count.textContent = shown === 1 ? '1 breed' : shown + ' breeds';
      if (this.empty) {
        this.empty.hidden = shown > 0;
        // Toggle the hint's own element. Never rewrite the merchant's sentence: concatenating onto
        // it duplicated the advice when their copy already mentioned the filter, and cached a
        // stale copy that survived clearing it. The merchant can also switch the filter off in the
        // sibling section, which this element cannot read - but whether a size is narrowing the
        // results right now is the question that actually matters, and it always knows that.
        const hint = this.querySelector('[data-breed-hub-size-hint]');
        if (hint) hint.hidden = this.size === 'All';
      }
    }
  }

  if (!customElements.get('breed-hub')) {
    customElements.define('breed-hub', BreedHub);
  }
})();
